import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Warehouse,
  Wrench,
  QrCode,
  ArrowRight,
  MapPin,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  HeartPulse
} from "lucide-react";
import { toast } from "sonner";

const FacilityDetails = () => {
  const { facilityId } = useParams();
  const { user, token } = useAuth();
  const [facility, setFacility] = useState(null);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [biomedDevices, setBiomedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [facilityId, token]);

  const fetchData = async () => {
    try {
      const [facilityRes, requestsRes] = await Promise.all([
        axios.get(`${API}/facilities/${facilityId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFacility(facilityRes.data);
      const facilityRequests = requestsRes.data.filter(r => r.location_type === "facility" && r.location_id === facilityId);
      setMaintenanceRequests(facilityRequests);
      if (facilityRes.data?.has_biomed_devices) {
        try {
          const devRes = await axios.get(`${API}/biomed-devices?location_type=facility&location_id=${facilityId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBiomedDevices(devRes.data || []);
        } catch { /* silent */ }
      }
    } catch (error) {
      toast.error("فشل في تحميل بيانات المرفق");
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = { pending: "قيد الانتظار", scheduled: "مجدول", in_progress: "قيد التنفيذ", completed: "مكتمل", cancelled: "ملغي" };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle2 className="w-4 h-4" />;
      case "in_progress": return <Clock className="w-4 h-4" />;
      case "pending": return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      wall: "الجدران", floor: "الأرضيات", ceiling: "السقف", lighting: "الإضاءة",
      plumbing: "السباكة", electrical: "الكهرباء", ac: "التكييف",
      medical_equipment: "أجهزة طبية أخرى", it_equipment: "جهاز إلكتروني", other: "أخرى"
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl text-slate-500">المرفق غير موجود</h2>
        <Link to="/facilities">
          <Button variant="outline" className="mt-4">
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للمرافق
          </Button>
        </Link>
      </div>
    );
  }

  const pendingRequests = maintenanceRequests.filter(r => r.status === "pending" || r.status === "in_progress");
  const completedRequests = maintenanceRequests.filter(r => r.status === "completed");

  return (
    <div className="space-y-6" data-testid="facility-details-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/facilities">
          <Button variant="ghost" className="gap-2">
            <ArrowRight className="w-4 h-4" />
            العودة للمرافق
          </Button>
        </Link>
      </div>

      {/* Facility Info Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-[#F5F3F9] flex items-center justify-center">
              <Warehouse className="w-10 h-10 text-[#655dc6]" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-[#655dc6] mb-2">{facility.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {facility.floor}
                </span>
                {facility.building && <span>• {facility.building}</span>}
                {facility.facility_type && (
                  <Badge variant="outline">{facility.facility_type}</Badge>
                )}
              </div>
              {facility.notes && (
                <p className="mt-3 text-slate-600">{facility.notes}</p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setQrDialogOpen(true)}
              className="gap-2"
            >
              <QrCode className="w-4 h-4" />
              رمز QR
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">{pendingRequests.length}</p>
                <p className="text-sm text-slate-500">طلبات قيد المعالجة</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">{completedRequests.length}</p>
                <p className="text-sm text-slate-500">طلبات مكتملة</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">{maintenanceRequests.length}</p>
                <p className="text-sm text-slate-500">إجمالي الطلبات</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Requests */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            طلبات الصيانة
          </CardTitle>
        </CardHeader>
        <CardContent>
          {maintenanceRequests.length > 0 ? (
            <div className="space-y-3">
              {maintenanceRequests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`status-${request.status}`}>
                          {getStatusIcon(request.status)}
                          <span className="mr-1">{getStatusLabel(request.status)}</span>
                        </Badge>
                        <Badge variant="outline">{getCategoryLabel(request.category)}</Badge>
                      </div>
                      <p className="text-slate-700">{request.description}</p>
                      <p className="text-sm text-slate-400 mt-2">
                        {new Date(request.created_at).toLocaleDateString("ar-SA")} - {request.created_by_name}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">لا توجد طلبات صيانة لهذا المرفق</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Biomedical Devices (only when the facility has the flag enabled) */}
      {facility.has_biomed_devices && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-emerald-700 flex items-center gap-2">
              <HeartPulse className="w-5 h-5" />
              الأجهزة الطبية بهذا المرفق
              <Badge variant="outline" className="mr-2">{biomedDevices.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {biomedDevices.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {biomedDevices.map((d) => (
                  <div key={d.id} className="p-3 rounded-lg border bg-slate-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono font-semibold text-[#4a3fa8]">{d.biomed_no}</span>
                      {d.latest_status && (
                        <Badge variant="outline" className="text-[10px]">{d.latest_status}</Badge>
                      )}
                    </div>
                    <p className="font-medium text-slate-800">{d.device_name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {d.manufacturer} — <span className="font-mono">{d.model_no}</span>
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <HeartPulse className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500 text-sm">لا توجد أجهزة مسجّلة بعد — من صفحة &quot;الأجهزة الطبية&quot; اختر هذا المرفق لإضافتها.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* QR Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">رمز QR - {facility.name}</DialogTitle>
            <DialogDescription>
              امسح هذا الرمز للإبلاغ عن مشكلة صيانة
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {facility.qr_code && (
              <img
                src={`data:image/png;base64,${facility.qr_code}`}
                alt={`QR Code for ${facility.name}`}
                className="w-48 h-48"
              />
            )}
            <p className="mt-4 text-center text-slate-500 text-sm">
              {facility.floor} • {facility.building}
            </p>
          </div>
          <Button
            onClick={async () => {
              await downloadQrCard({
                qrBase64: facility.qr_code,
                name: facility.name,
                floor: facility.floor,
                building: facility.building
              });
            }}
            className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white"
          >
            تحميل رمز QR
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityDetails;
